import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

// Bidcourt runs at /bidcourt/ on this domain (see vercel.json).
export const BIDCOURT_URL = "/bidcourt/";
export const STAFF_PHONE = { href: "tel:+917774853573", label: "+91 77748 53573" };
export const ADMIN_PHONE = { href: "tel:+919156156570", label: "+91 91561 56570" };
// Phone calls about leagues and custom plans. WhatsApp enquiries go to ADMIN_PHONE.
export const LEAGUES_CONTACT = { href: "tel:+918525880083", label: "+91 85258 80083" };
export const INSTAGRAM = { href: "https://instagram.com/vibeandvolley", label: "@vibeandvolley" };

// Location from the venue's Google Maps listing (place ChIJYVfKeQCZ2zsRrpzTh14OuKw).
const PIN = "19.8745529,75.3211743";
export const VENUE = {
  lines: ["Near ISKCON Temple, Chintamani Colony", "Chhatrapati Sambhajinagar, Maharashtra 431001"],
  directions: `https://www.google.com/maps/dir/?api=1&destination=${PIN}&destination_place_id=ChIJYVfKeQCZ2zsRrpzTh14OuKw`,
  embed: `https://maps.google.com/maps?q=${PIN}&z=16&output=embed`,
};

/** The logo sits on a white plate so it reads on any header colour. */
export function Logo({ size = "md" }) {
  return (
    <span className={`vv-logo vv-logo--${size}`}>
      <img src="/vv-logo.png" alt="Vibe &amp; Volley, The Pickleball District" />
    </span>
  );
}

/**
 * Scrolls to the top on a new page, or to the #section in the URL. React
 * Router does neither by itself, so /#rates from another page would land at
 * the top of Home.
 */
export function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Wait a frame so the page has rendered the section.
      const id = hash.slice(1);
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

const NAV = [
  { to: "/book", label: "Book a court" },
  { to: "/leagues", label: "Leagues & auctions" },
  { to: "/#rates", label: "Rates", hash: true },
  { to: "/#visit", label: "Visit us", hash: true },
];

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  return (
    <header className="vv-header" data-vv-surface="header">
      <div className="vv-wrap vv-header__row">
        <Link to="/" className="vv-header__home" aria-label="Vibe & Volley home">
          <Logo />
        </Link>

        <nav aria-label="Main" className={`vv-header__nav ${open ? "is-open" : ""}`} id="vv-main-nav">
          {NAV.map((item) =>
            item.hash ? (
              <Link key={item.to} to={item.to} className="vv-header__link">{item.label}</Link>
            ) : (
              <NavLink key={item.to} to={item.to} className="vv-header__link">{item.label}</NavLink>
            )
          )}
          <NavLink to="/manage" className="vv-header__link vv-header__link--quiet">Manage booking</NavLink>
        </nav>

        <div className="vv-header__actions">
          <Link to="/book" className="vv-btn vv-btn--accent vv-header__cta">Book a court</Link>
          <button
            type="button"
            className="vv-header__menu"
            aria-expanded={open}
            aria-controls="vv-main-nav"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen(!open)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}

export function SiteFooter({ variant = "site" }) {
  return (
    <footer className="vv-footer">
      <div className="vv-wrap vv-footer__row">
        <div className="vv-footer__brand">
          <Logo size="lg" />
          <span>The Pickleball District · by Tiny Tots Kindergarten</span>
          <span>{VENUE.lines.join(", ")}</span>
        </div>
        <nav aria-label="Footer" className="vv-footer__nav">
          <Link to="/book">Book a court</Link>
          <Link to="/manage">Manage booking</Link>
          <Link to="/leagues">Leagues &amp; auctions</Link>
          {variant === "leagues" && <a href={BIDCOURT_URL}>Organizer sign in</a>}
          <a href={INSTAGRAM.href} target="_blank" rel="noopener noreferrer">Instagram</a>
          <Link to="/login">Staff sign in</Link>
        </nav>
      </div>
    </footer>
  );
}

/** Compact dark bar for the staff, admin and sign-in pages. */
export function AppBar({ title, children }) {
  return (
    <header className="vv-appbar" data-vv-surface="header">
      <div className="vv-wrap vv-appbar__row">
        <Link to="/" aria-label="Vibe & Volley home"><Logo size="sm" /></Link>
        {title && <span className="vv-appbar__title">{title}</span>}
        <div className="vv-appbar__actions">{children}</div>
      </div>
    </header>
  );
}

/** The court drawing used in the hero. Colours come from the palette. */
export function CourtArt() {
  return (
    <svg viewBox="0 0 520 260" className="vv-court-art" aria-hidden="true">
      <rect x="0" y="0" width="520" height="260" rx="18" className="vv-court-art__frame" />
      <rect x="40" y="30" width="440" height="200" className="vv-court-art__court" />
      <rect x="190" y="30" width="140" height="200" className="vv-court-art__kitchen" />
      <g className="vv-court-art__lines">
        <rect x="40" y="30" width="440" height="200" fill="none" strokeWidth="3" />
        <line x1="260" y1="22" x2="260" y2="238" strokeWidth="4" />
        <line x1="190" y1="30" x2="190" y2="230" strokeWidth="3" />
        <line x1="330" y1="30" x2="330" y2="230" strokeWidth="3" />
        <line x1="40" y1="130" x2="190" y2="130" strokeWidth="3" />
        <line x1="330" y1="130" x2="480" y2="130" strokeWidth="3" />
      </g>
      <circle cx="398" cy="84" r="13" className="vv-court-art__ball" />
      <g className="vv-court-art__holes">
        <circle cx="393" cy="80" r="2" /><circle cx="402" cy="82" r="2" />
        <circle cx="396" cy="89" r="2" /><circle cx="404" cy="90" r="2" />
      </g>
    </svg>
  );
}

export function Check() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="vv-check">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

/** Message text without the leading ✅/❌/⚠️ the booking code uses as a status flag. */
export function messageTone(message) {
  if (!message) return null;
  if (message.startsWith("✅")) return "ok";
  if (message.startsWith("⚠")) return "warn";
  if (message.startsWith("❌")) return "error";
  return "info";
}

export function stripFlag(message) {
  return String(message || "").replace(/^(✅|❌|⚠️?)\s*/u, "");
}

export function Notice({ message, tone }) {
  if (!message) return null;
  const t = tone || messageTone(message);
  return (
    <div className={`vv-notice vv-notice--${t}`} role={t === "error" ? "alert" : "status"}>
      {stripFlag(message)}
    </div>
  );
}
